import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from './supabaseClient';

export type Plan = 'free' | 'starter' | 'pro' | 'team';

export interface TeamContext {
  team_id: string;
  team_name: string;
  role: 'owner' | 'admin' | 'member';
  billing_source: 'team_seat' | 'personal_pro';
  seat_limit: number;
  sub_status: string | null;
  cancel_at_period_end: boolean | null;
  current_period_end: number | null;
  parent_team_id: string | null;
}

interface PlanState {
  plan: Plan;
  planRenewsAt: string | null;
  loading: boolean;
  teams: TeamContext[];
  activeTeam: TeamContext | null;
}

const ACTIVE_STATUSES = new Set(['active', 'trialing']);

export function usePlan() {
  const { user, profile } = useAuth();
  const [state, setState] = useState<PlanState>({
    plan: 'free',
    planRenewsAt: null,
    loading: true,
    teams: [],
    activeTeam: null,
  });

  const reload = useCallback(async () => {
    if (!user) {
      setState({ plan: 'free', planRenewsAt: null, loading: false, teams: [], activeTeam: null });
      return;
    }

    const personalPlan = ((profile as unknown as { plan?: Plan })?.plan || 'free') as Plan;
    const personalRenews = ((profile as unknown as { plan_renews_at?: string })?.plan_renews_at) || null;

    let teams: TeamContext[] = [];
    try {
      const { data } = await supabase.rpc('get_my_team_context');
      if (data && typeof data === 'object' && 'teams' in data) {
        teams = ((data as { teams: TeamContext[] }).teams) || [];
      }
    } catch (e) {
      console.warn('get_my_team_context failed', e);
    }

    const activeTeamSeat = teams.find(
      (t) => t.billing_source === 'team_seat' && t.sub_status && ACTIVE_STATUSES.has(t.sub_status),
    );
    const overflowPro = teams.find((t) => t.billing_source === 'personal_pro');
    const currentTeamId = (profile as unknown as { current_team_id?: string })?.current_team_id;
    const activeTeam =
      teams.find((t) => t.team_id === currentTeamId) ||
      activeTeamSeat ||
      overflowPro ||
      teams[0] ||
      null;

    let effectivePlan: Plan = personalPlan;
    if (activeTeamSeat) effectivePlan = 'team';
    else if (overflowPro && (effectivePlan === 'free' || effectivePlan === 'starter')) effectivePlan = 'pro';

    setState({
      plan: effectivePlan,
      planRenewsAt: personalRenews,
      loading: false,
      teams,
      activeTeam,
    });
  }, [user, profile]);

  useEffect(() => {
    reload();
  }, [reload]);

  const isTeam = state.plan === 'team';
  const isPro = state.plan === 'pro' || isTeam;
  const isStarter = state.plan === 'starter';
  const isPaid = isPro || isStarter;

  const hasCertificates = isPro;
  const hasPublicProfile = isPro;
  const hasFullSkillPaths = isPro;

  return {
    ...state,
    isPro,
    isStarter,
    isTeam,
    isPaid,
    hasCertificates,
    hasPublicProfile,
    hasFullSkillPaths,
    reload,
  };
}
